import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useHelpRequests } from '@/hooks/useConsoleData';
import { closeHelpRequest, draftFor, isOpen, sendHelpReply, stageHelpReply, waitedLine, whereFrom, whoWrote, withSuggestedTime, type HelpFilter, type HelpList, type HelpRow } from '@/lib/help-data';
import { num, when } from '@/lib/format';
import s from './console.module.css';

/**
 * Help (2026-10-17): every press of the in-app help button, oldest unanswered
 * first, each with the reply staged on it. The staged reply is a draft
 * written by us or by Brian; he edits it here, "Suggest a time" adds the
 * booking line, Save keeps the draft on the row, and the one Send button
 * mails it through the product's own mailer with Reply-To Brian and marks the
 * row answered. Nothing on this page sends by itself.
 *
 * Why this page exists: five people wrote between Sept 21 and Oct 6 and the
 * only trace was a push to a phone. The row now says how long each person
 * has waited, and an answered row keeps the reply that went, so the thread
 * reads as one months later.
 */
const FILTERS: { key: HelpFilter; label: string }[] = [
  { key: 'new', label: 'Unanswered' },
  { key: 'answered', label: 'Answered' },
  { key: 'all', label: 'All' },
];

export default function HelpPage() {
  const [filter, setFilter] = useState<HelpFilter>('new');
  const help = useHelpRequests(filter);
  const qc = useQueryClient();
  const d = help.data;
  const refresh = () => void qc.invalidateQueries({ queryKey: ['help-requests'] });

  return (
    <div className={s.stack}>
      <div className={s.pageHead}>
        <h1 className={s.pageTitle}>Help</h1>
        <span className={s.pageSub}>
          {d ? `${num(d.counts.new)} unanswered · ${num(d.counts.answered)} answered · replies go out from the product mailer, replies come back to ${d.replyFrom} · refreshed ${when(d.generatedAt)}` : ''}
        </span>
      </div>

      {help.isError && <div className={s.error}>Could not load the help requests. This page needs GET /provider/admin/help-requests (pv-auth #433).</div>}

      <div className={`${s.filters} ${s.scrollRow}`} role="tablist" aria-label="Which requests">
        {FILTERS.map((f) => (
          <button key={f.key} type="button" role="tab" aria-selected={filter === f.key} className={`${s.filter} ${filter === f.key ? s.filterOn : ''}`} onClick={() => setFilter(f.key)}>
            {f.label}{d ? ` (${num(d.counts[f.key])})` : ''}
          </button>
        ))}
      </div>

      <div className={s.stack} data-panel="help-rows">
        {d && d.rows.length === 0 && <div className={s.empty}>{filter === 'new' ? 'Nobody is waiting. Every help request has a reply.' : 'Nothing here yet.'}</div>}
        {d && d.rows.map((row) => <HelpRequestCard key={row.id} row={row} list={d} onChange={refresh} />)}
      </div>
    </div>
  );
}

/**
 * One request and its reply. The fields start from the staged reply; edits
 * live in the textarea until Save (back onto the row) or Send. Answered rows
 * show the reply that went and no composer.
 */
export function HelpRequestCard({ row, list, onChange }: { row: HelpRow; list: Pick<HelpList, 'suggestATime' | 'bookingUrl'>; onChange: () => void }) {
  const start = draftFor(row);
  const [subject, setSubject] = useState(start.subject);
  const [body, setBody] = useState(start.body);
  const [busy, setBusy] = useState<'save' | 'send' | 'close' | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const open = isOpen(row);
  const edited = subject !== start.subject || body !== start.body;

  const save = async () => {
    setBusy('save');
    setNote(null);
    try {
      await stageHelpReply(row.id, { subject, body });
      setNote(body.trim() ? 'Draft saved on the request. Nothing was sent.' : 'Draft cleared. Nothing was sent.');
      onChange();
    } catch (err) {
      setNote(`Could not save the draft: ${(err as Error).message}`);
    } finally {
      setBusy(null);
    }
  };

  const send = async () => {
    if (!body.trim()) { setNote('Nothing to send: write the reply first.'); return; }
    if (!row.replyTo) { setNote('Nowhere to send: this person left no email address and has no account.'); return; }
    if (!window.confirm(`Send this reply to ${row.replyTo} now?`)) return;
    setBusy('send');
    setNote(null);
    try {
      const r = await sendHelpReply(row.id, { subject, body });
      setNote(r.ok ? `Sent to ${r.sentTo}. The request is marked answered.` : r.message);
      if (r.ok) onChange();
    } catch (err) {
      setNote(`Not sent: ${(err as Error).message}`);
    } finally {
      setBusy(null);
    }
  };

  const suggestTime = () => setBody((cur) => withSuggestedTime(cur, list.suggestATime));

  // For a duplicate, the form's own error text or a test row: nothing is mailed, the reason is logged.
  const close = async () => {
    const reason = window.prompt('Close without sending. Why? (kept in the log)', '');
    if (!reason || !reason.trim()) return;
    setBusy('close');
    setNote(null);
    try {
      await closeHelpRequest(row.id, reason.trim());
      setNote('Closed. Nothing was sent.');
      onChange();
    } catch (err) {
      setNote(`Could not close it: ${(err as Error).message}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className={`${s.panel} ${open ? s.toldRowUnread : ''}`} data-help-id={row.id} data-help-status={row.status}>
      <div className={s.panelHead}>
        <span className={s.panelTitle}>
          {row.userId ? <Link to={`/people/${row.userId}`}>{whoWrote(row)}</Link> : whoWrote(row)}
          {' '}<span className={`${s.pill} ${open ? s.pill_unread : s.pill_sent}`}>{open ? 'unanswered' : row.status === 'ANSWERED' ? 'answered' : 'closed'}</span>
        </span>
        <span className={s.sub}>{whereFrom(row)} · {when(row.createdAt)} · {waitedLine(row)}{row.plan ? ` · ${row.plan}` : ''}</span>
      </div>
      <div className={s.sub}>
        {row.replyTo ? `Reply goes to ${row.replyTo}` : 'No address to reply to'}
        {row.account && row.replyTo && row.account.email.toLowerCase() !== row.replyTo.toLowerCase() ? ` (account: ${row.account.email})` : ''}
        {row.phone ? ` · phone on file` : ''}
        {!row.userId ? ' · written while signed out' : ''}
        {row.notifiedAt ? '' : ` · the push never reached a phone${row.notifyError ? ` (${row.notifyError})` : ''}`}
      </div>
      <blockquote className={s.toldText} data-field="concern" style={{ whiteSpace: 'pre-wrap', margin: '8px 0' }}>{row.concern}</blockquote>

      {open ? (
        <div className={s.draft} data-panel="help-reply">
          <div className={s.draftHead}>
            <strong>Staged reply</strong>
            <span className={s.draftHeadRight}>
              <span className={s.sub}>{row.staged ? `drafted ${when(row.staged.at)}${row.staged.by ? ` by ${row.staged.by}` : ''}` : 'nothing staged yet'}</span>
            </span>
          </div>
          <input className={s.draftSubject} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject (blank means: Your message to PostureVision)" aria-label="Subject" />
          <textarea className={s.draftBody} value={body} onChange={(e) => setBody(e.target.value)} rows={12} aria-label="Reply" placeholder="In your words. This goes to the person when you press Send." />
          <div className={s.draftActions}>
            <button type="button" className={s.btn} disabled={busy !== null || !body.trim() || !row.replyTo} onClick={() => void send()} data-action="send">
              {busy === 'send' ? 'Sending' : 'Send'}
            </button>
            <button type="button" className={s.btnGhost} disabled={busy !== null || (!edited && !!row.staged)} onClick={() => void save()} data-action="save">
              {busy === 'save' ? 'Saving' : 'Save draft (sends nothing)'}
            </button>
            <button type="button" className={s.linkBtn} disabled={busy !== null} onClick={suggestTime} data-action="suggest-time" title={list.bookingUrl ? `Adds the booking link ${list.bookingUrl}` : 'Adds a line asking for two times that suit them'}>
              Suggest a time
            </button>
            {edited && row.staged && <button type="button" className={s.linkBtn} onClick={() => { setSubject(start.subject); setBody(start.body); }}>Back to the staged text</button>}
            <button type="button" className={s.linkBtn} disabled={busy !== null} onClick={() => void close()} data-action="close" title="For a duplicate, the form's own error text or a test row">
              {busy === 'close' ? 'Closing' : 'Close without sending'}
            </button>
          </div>
          {row.answerError && <div className={s.jWarn}>The last send failed: {row.answerError}</div>}
          {note && <div className={s.sub} data-field="note">{note}</div>}
        </div>
      ) : (
        row.answered && (
          <div className={s.draft} data-panel="help-answer">
            <div className={s.draftHead}>
              <strong>{row.answered.body ? 'Reply sent' : 'Closed without sending'} {when(row.answered.at)}{row.answered.by ? ` by ${row.answered.by}` : ''}</strong>
            </div>
            {row.answered.subject && <div className={s.sub}>Subject: {row.answered.subject}</div>}
            {row.answered.body && <div className={s.toldText} style={{ whiteSpace: 'pre-wrap' }}>{row.answered.body}</div>}
          </div>
        )
      )}
    </div>
  );
}
